import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography
} from "@mui/material";
import { useAuth } from "./auth/AuthContext";

const apps = [
  { title: "Leopardy", to: "/leopardy", blurb: "Classroom buzzer game hub.", tag: "Games" },
  { title: "Leopardy Games", to: "/leopardy/games", blurb: "Host or manage games.", tag: "Games", auth: true },
  { title: "Profile", to: "/profile", blurb: "Account and session info.", tag: "Account", auth: true },
  { title: "Food Pairing Lab", to: "/food-pairing", blurb: "Firestore-backed flavor pairings.", tag: "Kitchen", auth: true },
  { title: "Babble", to: "/babble", blurb: "Word grid builder.", tag: "Games" },
  { title: "Connections", to: "/connections", blurb: "Match-themes puzzle.", tag: "Games" },
  { title: "Schmorg", to: "/schmorg", blurb: "Story generator sandbox.", tag: "Fun" },
  { title: "NaNoKingORamaAthon", to: "/nanoking", blurb: "NaNo helper tools.", tag: "Writing", auth: true },
  { title: "DataViz", to: "/dataviz", blurb: "Charts and experiments.", tag: "Data" },
  { title: "Chadventure", to: "/oddventure", blurb: "Oddventure tables.", tag: "Games", auth: true },
  { title: "Binaural Beats", to: "/binaural-beats", blurb: "Mix dual tones with presets and live controls.", tag: "Audio" },
  { title: "Culture Club", to: "/culture-club", blurb: "Fermentation, starter swaps, and culture lore.", tag: "Kitchen" },
  { title: "Lifelapse Feature", to: "/lifelapse", blurb: "Stop-motion feature page with ghost image + sound workflow.", tag: "Feature" },
  { title: "Christmas Counter", to: "/christmas-counter", blurb: "Days-until tracker.", tag: "Seasonal", auth: true },
  { title: "Wademon", to: "/wademon", blurb: "Creature builder.", tag: "Games" },
  { title: "TOLOPOSOGO", to: "/toloposogo", blurb: "TO to GO guided thinking with stage graph.", tag: "Thinking" },
  { title: "Quilt Graph", to: "/graph", blurb: "Unified nodes/edges substrate and type graph explorer.", tag: "Graph", auth: true },
  { title: "Quilt Docs", to: "/docs", blurb: "Structured-span docs with node/edge refs.", tag: "Docs", auth: true },
  { title: "Janki", to: "/srs", blurb: "FSRS-backed decks, import, and daily study.", tag: "Learning", auth: true },
  { title: "Cortisolace (If/Then/When)", to: "/cortisolace", blurb: "Owner-run 365-day trigger calendar.", tag: "Routines", auth: true, ownerOnly: true },
];

export default function App() {
  const { user } = useAuth();
  const providerEmails = (user?.providerData || [])
    .map((p) => p.email)
    .filter(Boolean)
    .map((e) => e.toLowerCase());
  const effectiveEmail = (user?.email || providerEmails[0] || "").toLowerCase();
  const canSeeOwnerOnly = effectiveEmail === "dunderjeep@gmail.com" || user?.uid === "VvBUVY6rN5Xg358oNXW2eoUzQX92";
  const isAuthed = !!user && !user.isAnonymous;

  const visibleApps = useMemo(() => {
    return apps.filter((app) => {
      if (app.ownerOnly && !canSeeOwnerOnly) return false;
      if (app.auth && !isAuthed) return false;
      return true;
    });
  }, [canSeeOwnerOnly, isAuthed]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "radial-gradient(circle at 10% 20%, #e8ecff 0, #f7f7fb 30%, #fef6ef 75%)",
        pb: 8,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 8, pb: 4 }}>
        <Stack spacing={4}>
          <Stack spacing={2}>
            <Chip
              label="DunderJeep Studio"
              sx={{
                alignSelf: "flex-start",
                fontWeight: 700,
                letterSpacing: 0.5,
                background: "#111827",
                color: "#f8fafc",
              }}
            />
            <Typography variant="h3" fontWeight={800} lineHeight={1.1}>
              Explore the projects
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 720 }}>
              A quick launch pad for all the experiments, games, and utilities we are building. Pick an app to dive in.
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                component={Link}
                to="/leopardy"
                variant="contained"
                size="large"
                sx={{
                  background: "linear-gradient(120deg, #6366f1, #8b5cf6)",
                  borderRadius: 2,
                  fontWeight: 700,
                  px: 3,
                }}
              >
                Launch Leopardy
              </Button>
              <Button
                component={Link}
                to="/food-pairing"
                variant="outlined"
                size="large"
                sx={{ borderRadius: 2, fontWeight: 700 }}
              >
                Food Pairing Lab
              </Button>
            </Stack>
          </Stack>

          <Grid container spacing={3}>
            {visibleApps.map((app) => (
              <Grid item xs={12} sm={6} md={4} key={app.title}>
                <Card
                  elevation={4}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 3,
                    border: "1px solid rgba(0,0,0,0.06)",
                    background: "#fff",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                      <Chip
                        label={app.tag}
                        size="small"
                        sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}
                      />
                      {app.auth && (
                        <Chip label="Auth" size="small" color="primary" variant="outlined" />
                      )}
                    </Stack>
                    <Typography variant="h6" fontWeight={800} gutterBottom>
                      {app.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {app.blurb}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ px: 2, pb: 2 }}>
                    <Button
                      component={Link}
                      to={app.to}
                      fullWidth
                      variant="contained"
                      sx={{
                        borderRadius: 2,
                        fontWeight: 700,
                        textTransform: "none",
                        background: "linear-gradient(135deg, #0ea5e9, #22c55e)",
                      }}
                    >
                      Open
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}
